import BizFormEnvService from "../../../services/env-service";

/**
 * Normalizes a BizForm API root URL so controller segments concatenate safely.
 */
export function normalizeBizFormApiBaseUrl(baseUrl: string): string {
    const trimmed = (baseUrl ?? "").trim();
    if (!trimmed) {
        return "";
    }
    return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
}

export interface BizFormApiEndpoints {
    baseUrl: string;
    form: {
        create: string;
        update: string;
        delete: (id: any) => string;
        get: (id: any) => string;
        list: string;
        simpleList: string;
    };
    response: {
        submit: string;
        update: string;
        review: string;
        delete: (id: any) => string;
        get: (id: any) => string;
        list: string;
    };
    externalUser: {
        getOrCreate: string;
    };
    externalUserBased: {
        getForm: string;
        submitForExternalUser: string;
        listFormResponses: string;
        getResponse: string;
    };
    setting: {
        getPartnerSettings: string;
        updatePartnerSetting: string;
        resetToDefaultPartnerSetting: string;
    };
    reportingTools: {
        getColumns: (reportName: string) => string;
        lookup: (apiKey: string, keyword?: string) => string;
    };
    question: {
        create: string;
        delete: (id: any) => string;
        deleteOptions: (id: any) => string;
        update: string;
        changeIndex: string;
    };
    option: {
        create: string;
        delete: (optionId: any) => string;
        update: string;
        changeIndex: string;
    };
    bizFile: {
        baseUrl: string;
        uploadRemainingChunk: string;
        createAccessToken: string;
        uploadFile: string;
        deleteFile: (id: any) => string;
        createStorageAccount: string;
        getStorageAccount: string;
    };
}

/**
 * Builds absolute endpoint paths for a given BizForm API base URL.
 */
export function buildBizFormApiEndpoints(rawBaseUrl: string): BizFormApiEndpoints {
    const baseUrl = normalizeBizFormApiBaseUrl(rawBaseUrl) || normalizeBizFormApiBaseUrl(BizFormEnvService.ApiUrl);
    const controllers = {
        form: `${baseUrl}Form`,
        question: `${baseUrl}Question`,
        option: `${baseUrl}Option`,
        bizFile: `${baseUrl}BizFile`,
        response: `${baseUrl}Response`,
        externalUser: `${baseUrl}ExternalUser`,
        externalUserBased: `${baseUrl}ExternalUserBased`,
        setting: `${baseUrl}Setting`,
        reportingTools: `${baseUrl}ReportingTools`,
    };
    return {
        baseUrl,
        form: {
            create: `${controllers.form}/CreateForm`,
            update: `${controllers.form}/UpdateForm`,
            delete: (id: any) => `${controllers.form}/DeleteForm/${id.toString()}`,
            get: (id: any) => `${controllers.form}/GetForm/${id.toString()}`,
            list: `${controllers.form}/ListForms`,
            simpleList: `${controllers.form}/SimpleListForm`,
        },
        response: {
            submit: `${controllers.response}/SubmitResponse`,
            update: `${controllers.response}/UpdateResponse`,
            review: `${controllers.response}/ReviewResponse`,
            delete: (id: any) => `${controllers.response}/DeleteResponse/${id.toString()}`,
            get: (id: any) => `${controllers.response}/GetResponse/${id.toString()}`,
            list: `${controllers.response}/ListFormResponses`,
        },
        externalUser: {
            getOrCreate: `${controllers.externalUser}/GetOrCreate`,
        },
        externalUserBased: {
            submitForExternalUser: `${controllers.externalUserBased}/SubmitResponseForExternalUser`,
            getForm: `${controllers.externalUserBased}/GetFormForExternalUser`,
            listFormResponses: `${controllers.externalUserBased}/ListFormResponsesForExternalUser`,
            getResponse: `${controllers.externalUserBased}/GetResponseForExternalUser`,
        },
        setting: {
            getPartnerSettings: `${controllers.setting}/GetPartnerSettings`,
            updatePartnerSetting: `${controllers.setting}/UpdatePartnerSetting`,
            resetToDefaultPartnerSetting: `${controllers.setting}/ResetToDefaultPartnerSetting`,
        },
        reportingTools: {
            getColumns: (reportName: string) =>
                `${controllers.reportingTools}/GetColumns?reportName=${encodeURIComponent(reportName)}`,
            lookup: (apiKey: string, keyword?: string) => {
                const params = new URLSearchParams({apiKey});
                if (keyword != null && keyword !== '') {
                    params.set('keyword', keyword);
                }
                return `${controllers.reportingTools}?${params.toString()}`;
            },
        },
        question: {
            create: `${controllers.question}/CreateQuestion`,
            delete: (id: any) => `${controllers.question}/DeleteQuestion/${id.toString()}`,
            deleteOptions: (id: any) => `${controllers.question}/DeleteQuestionOptions/${id.toString()}`,
            update: `${controllers.question}/UpdateQuestion`,
            changeIndex: `${controllers.question}/ChangeQuestionIndex`,
        },
        option: {
            create: `${controllers.option}/CreateOption`,
            delete: (optionId: any) => `${controllers.option}/DeleteOption/${optionId.toString()}`,
            update: `${controllers.option}/UpdateOption`,
            changeIndex: `${controllers.option}/ChangeOptionIndex`,
        },
        bizFile: {
            baseUrl: controllers.bizFile,
            uploadRemainingChunk: `${controllers.bizFile}/UploadRemainingChunk`,
            createAccessToken: `${controllers.bizFile}/CreateAccessToken`,
            uploadFile: `${controllers.bizFile}/SmallFileOrInitiateUpload`,
            deleteFile: (id: any) => `${controllers.bizFile}/${id.toString()}`,
            createStorageAccount: `${controllers.bizFile}/CreateStorageAccount`,
            getStorageAccount: `${controllers.bizFile}/GetStorageAccount`,
        },
    };
}
